import { motion } from "framer-motion";
import { METRICS } from "./data";
import { CountUp, EASE, Eyebrow, MaskLines, Reveal } from "./ui-bits";

const SCAN_LABELS = [
  { text: "VISIBILITY 92", pos: "left-[8%] top-[16%]", delay: 0.6 },
  { text: "FOOTFALL 95", pos: "right-[8%] top-[36%]", delay: 0.9 },
  { text: "ACCESSIBILITY 86", pos: "left-[10%] top-[56%]", delay: 1.2 },
  { text: "CATCHMENT 91", pos: "right-[12%] top-[72%]", delay: 1.5 },
  { text: "RENTAL ECONOMICS 79", pos: "left-[14%] top-[86%]", delay: 1.8 },
];

const MODULES = [
  {
    n: "01",
    t: "Property Matching",
    c: "Match location, size, budget, frontage, footfall and business requirements.",
  },
  {
    n: "02",
    t: "Tenant Intelligence",
    c: "Identify occupiers most aligned with your property's potential.",
  },
  {
    n: "03",
    t: "Market Intelligence",
    c: "Understand rents, vacancies, developments and micro-market movements.",
  },
  {
    n: "04",
    t: "Off-Market Access",
    c: "Discover opportunities beyond publicly advertised inventory.",
  },
];

const Intelligence = () => (
  <section
    id="intelligence"
    className="border-y border-ivory/5 bg-charcoal"
    data-testid="intelligence-section"
  >
    <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36">
      <Eyebrow tone="champagne">Elevate Intelligence™</Eyebrow>
      <h2 className="mt-6 font-serif text-4xl leading-[1.02] text-ivory sm:text-5xl lg:text-6xl">
        <MaskLines lines={["REAL ESTATE", "WITH INTELLIGENCE."]} />
      </h2>
      <Reveal delay={0.2}>
        <p className="mt-6 text-[12px] font-medium uppercase tracking-[0.24em] text-stone">
          Data-driven. Relationship-led. Technology-enabled.
        </p>
      </Reveal>

      <div className="mt-16 grid gap-10 lg:grid-cols-2">
        {/* Scan visual */}
        <Reveal className="relative overflow-hidden" data-testid="intelligence-visual">
          <div className="relative aspect-[4/5] max-h-[640px] w-full overflow-hidden">
            <img
              src="/assets/img/pexels-volkerthimm-27307398.jpg"
              alt="Architectural study"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-obsidian/35" />
            <div className="animate-scan absolute left-0 h-[2px] w-full bg-gradient-to-r from-transparent via-champagne to-transparent shadow-[0_0_24px_rgba(183,162,122,0.7)]" />
            {SCAN_LABELS.map((l) => (
              <motion.div
                key={l.text}
                className={`absolute ${l.pos} border border-champagne/40 bg-obsidian/80 px-3 py-1.5 text-[10px] font-medium tracking-[0.22em] text-champagne backdrop-blur-sm`}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-15%" }}
                transition={{ duration: 0.7, delay: l.delay, ease: EASE }}
              >
                {l.text}
              </motion.div>
            ))}
          </div>
        </Reveal>

        {/* Dashboard */}
        <Reveal delay={0.15}>
          <div
            className="flex h-full flex-col border border-ivory/10 bg-obsidian/60 p-6 md:p-10"
            data-testid="property-dashboard"
          >
            <div className="flex items-center justify-between text-[10px] font-medium uppercase tracking-[0.28em] text-stone">
              <span>Property Intelligence</span>
              <span className="text-ivory/70">Indiranagar · 2,400 sq ft</span>
            </div>

            <div className="mt-10 flex items-end gap-4">
              <span className="font-serif text-7xl leading-none text-ivory md:text-8xl">
                <CountUp to={89} />
              </span>
              <span className="pb-2 font-serif text-2xl italic text-stone">/ 100</span>
            </div>
            <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.28em] text-champagne">
              Elevate Property Score™
            </div>

            <div className="mt-10 space-y-5">
              {METRICS.map((m, i) => (
                <div key={m.label} className="grid grid-cols-[130px_1fr_36px] items-center gap-4">
                  <span className="text-[11px] uppercase tracking-[0.16em] text-stone">
                    {m.label}
                  </span>
                  <span className="relative h-px bg-ivory/10">
                    <motion.span
                      className="absolute left-0 top-0 h-px bg-champagne"
                      initial={{ width: "0%" }}
                      whileInView={{ width: `${m.value}%` }}
                      viewport={{ once: true, margin: "-10%" }}
                      transition={{ duration: 1.6, delay: 0.2 + i * 0.1, ease: EASE }}
                    />
                  </span>
                  <span className="text-right text-xs text-ivory/90">
                    <CountUp to={m.value} duration={1.6} />
                  </span>
                </div>
              ))}
            </div>

            <p className="mt-auto pt-8 text-[10px] italic tracking-[0.08em] text-stone/60">
              Illustrative property assessment.
            </p>
          </div>
        </Reveal>
      </div>

      {/* Modules */}
      <div className="mt-16 grid gap-px border border-ivory/10 bg-ivory/10 md:grid-cols-2 lg:grid-cols-4">
        {MODULES.map((m, i) => (
          <Reveal key={m.n} delay={i * 0.08} className="bg-charcoal">
            <div className="h-full p-6 md:p-8" data-testid={`intelligence-module-${m.n}`}>
              <span className="font-serif text-sm italic text-champagne">{m.n}</span>
              <h3 className="mt-4 text-sm font-medium uppercase tracking-[0.18em] text-ivory">
                {m.t}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-stone">{m.c}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Intelligence;
